/**
 * Adapter service towards webmate Artifact API.
 */
import {Injectable} from '@angular/core';
import {EventsService} from './events/events.service';
import {ArtifactApi} from '../api/rest/artifact.restapi';
import {ArtifactId, ExpeditionId, BrowserSessionStateId, ProjectId, TestRunId} from '../types/ids';
import {forkJoin, Observable, of, ReplaySubject, Subject, Subscription, timer} from 'rxjs';
import {isDefined} from '../utils/utils';
import {map, mergeMap, switchMap, take, filter, takeUntil, shareReplay, finalize, tap, takeWhile} from 'rxjs/operators';
import {Artifact, ArtifactInfo, ArtifactType, WellKnownArtifactEvents} from '../types/artifact';
import {TestType} from '@webmate-web-core/shared/types/testmgmt';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {ApiSourcedPaginationResult} from '@webmate-web-core/shared/api/apibase.api';

@Injectable({
    providedIn: 'root'
})
export class ArtifactService {

    private _artifactById: Map<string /* ArtifactId */, Subject<Artifact>> = new Map<string /* ArtifactId */, ReplaySubject<Artifact>>();
    private _artifactUpdates: { [key: string]: Subscription } = {};

    private _stateEndUpdates = new Subject<ArtifactInfo>();

    constructor(private artifactApi: ArtifactApi, private eventsService: EventsService) {
        this.eventsService.getAllEventsOfType([WellKnownArtifactEvents.ArtifactAdded, WellKnownArtifactEvents.ArtifactUpdated]).subscribe((event) => {
            if (event.eventType === WellKnownArtifactEvents.ArtifactAdded) {
                event.artifacts.forEach((artifact: ArtifactInfo) => {
                    if (artifact.artifactType === 'Action.BrowserSessionStateEnd') {
                        this._stateEndUpdates.next(artifact);
                    }

                    this._updateArtifactCache(artifact.id);
                });
            }

            if (event.eventType === WellKnownArtifactEvents.ArtifactUpdated) {
                const artifact = event.artifact as ArtifactInfo;
                this._updateArtifactCache(artifact.id);
            }
        });
    }

    _updateArtifactCache(id: ArtifactId): void {
        if (isDefined(this._artifactById.get(id))) {
            if (this._artifactUpdates[id]) {
                this._artifactUpdates[id].unsubscribe();
                delete this._artifactUpdates[id];
            }
            this._artifactUpdates[id] = this.artifactApi.getArtifact$(id).subscribe((artifact) => {
                this._artifactById.get(artifact.id).next(artifact);
            });
        }
    }

    addArtifact(projectId: ProjectId, artifactType: ArtifactType, associatedTestRun: TestRunId, data: any): Observable<ArtifactId> {
        return this.addArtifacts(projectId, artifactType, associatedTestRun ? [associatedTestRun] : [], data);
    }

    addArtifacts(projectId: ProjectId, artifactType: ArtifactType, associatedTestRuns: TestRunId[], data: any): Observable<ArtifactId> {
        return this.artifactApi.addArtifact(projectId, artifactType, associatedTestRuns, data);
    }

    getArtifact$(id: ArtifactId): Observable<Artifact> {
        if (!isDefined(this._artifactById.get(id))) {
            let sessionSubject = new ReplaySubject<Artifact>(1);
            this._artifactById.set(id, sessionSubject);
            return this.artifactApi.getArtifact$(id).pipe(map((dto) => {
                sessionSubject.next(dto);
            }), mergeMap(_ => this._artifactById.get(id).asObservable()));
        }
        return this._artifactById.get(id).asObservable();
    }

    refreshArtifact$(id: ArtifactId): Observable<Artifact> {
        return this.artifactApi.getArtifact$(id);
    }

    getArtifacts$(ids: Array<ArtifactId>): Observable<Artifact[]> {
        // TODO: caching
        if (ids.length === 0) {
            return of([]);
        } else {
            return forkJoin(ids.map(id => this.getArtifact$(id!).pipe(take(1)))).pipe(map(ar => ar), take(1));
        }
    }

    // get artifacts in the given state. Poll every 5 seconds until the state is finished, and
    // emit whenever new artifacts are added to the state.
    getArtifactsInState$(stateId: BrowserSessionStateId): Observable<Artifact[]> {
        const POLL_EVERY_MS = 5000; // 5 seconds

        /** Emits 0 immediately, then 1, 2, 3… every POLL_EVERY_MS */
        const tick$ = timer(0, POLL_EVERY_MS);

        /** One HTTP call per tick */
        const poll$ = tick$.pipe(
            switchMap(() => this.artifactApi.getArtifactsInState$(stateId)),
            shareReplay({bufferSize: 1, refCount: true}),     // cache latest
        );

        return poll$.pipe(
            takeWhile(list => !list.some(a => a.artifactType === 'Action.BrowserSessionStateEnd'), true), // stop polling & include the latest response                         
        );
    }

    subscribeBrowserSessionStateEndArtifacts$(): Observable<ArtifactInfo> {
        return this._stateEndUpdates.asObservable();
    }

    getArtifactsInBrowserSession$(id: ExpeditionId, optLimit?: number, optStartTime?: DateTime): Observable<ArtifactInfo[]> {
        return this.artifactApi.getBrowserSessionArtifacts$(id, optLimit, optStartTime).pipe(map((dtos) => dtos ?? []));
    }

    getArtifactsInProject$(projectId: ProjectId, optTestRunId: TestRunId | undefined, optTypes?: Array<ArtifactType>): Observable<Array<ArtifactInfo>> {
        return this.artifactApi.getArtifactsInProject$(projectId, optTestRunId, optTypes);//.pipe(map(res => List(res)));
    }

    getArtifactInfosInProjectPage$(
        projectId: ProjectId,
        testRunId: TestRunId,
        types: ArtifactType[],
        limit: number,
        offset: number
    ): Observable<ApiSourcedPaginationResult<ArtifactInfo>> {
        return this.artifactApi.getArtifactInfosInProjectPage$(projectId, testRunId, types, limit, offset);
    }
}
